interface PaginationProps {
  page: number;
  pageCount: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

const PAGE_SIZES = [10, 20, 50];

export default function Pagination({
  page,
  pageCount,
  total,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: PaginationProps) {
  if (total === 0) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const pages: number[] = [];
  const start = Math.max(1, page - 2);
  const end = Math.min(pageCount, start + 4);
  for (let i = start; i <= end; i += 1) pages.push(i);

  return (
    <div className="flex flex-col gap-3 border-t border-background-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-xs text-foreground-500">
          Mostrando <strong className="text-foreground-800">{from}</strong>–
          <strong className="text-foreground-800">{to}</strong> de{' '}
          <strong className="text-foreground-800">{total}</strong>
        </p>
        {onPageSizeChange && (
          <label className="flex items-center gap-1.5 text-xs text-foreground-500">
            Filas:
            <select
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
              className="h-8 cursor-pointer rounded-md border border-background-300 bg-background-50 px-2 text-xs text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Página anterior"
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <i className="ri-arrow-left-s-line text-base"></i>
        </button>

        {start > 1 && (
          <>
            <button
              type="button"
              onClick={() => onPageChange(1)}
              className="h-8 min-w-8 cursor-pointer rounded-md border border-background-200 px-2 text-xs text-foreground-600 transition-colors hover:bg-background-100"
            >
              1
            </button>
            {start > 2 && <span className="px-1 text-xs text-foreground-400">…</span>}
          </>
        )}

        {pages.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => onPageChange(value)}
            className={[
              'h-8 min-w-8 cursor-pointer rounded-md border px-2 text-xs font-medium transition-colors',
              value === page
                ? 'border-primary-500 bg-primary-500 text-background-50 dark:text-foreground-950'
                : 'border-background-200 text-foreground-600 hover:bg-background-100',
            ].join(' ')}
          >
            {value}
          </button>
        ))}

        {end < pageCount && (
          <>
            {end < pageCount - 1 && <span className="px-1 text-xs text-foreground-400">…</span>}
            <button
              type="button"
              onClick={() => onPageChange(pageCount)}
              className="h-8 min-w-8 cursor-pointer rounded-md border border-background-200 px-2 text-xs text-foreground-600 transition-colors hover:bg-background-100"
            >
              {pageCount}
            </button>
          </>
        )}

        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount}
          aria-label="Página siguiente"
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <i className="ri-arrow-right-s-line text-base"></i>
        </button>
      </div>
    </div>
  );
}